import React, { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { Button } from './Button';
import './Calendar.css';

export interface CalendarEvent {
    id: number;
    title: string;
    date: string;
    type: 'precall' | 'postcall' | 'visit';
    status?: string;
    customer?: string;
    salesman?: string;
}

interface CalendarProps {
    events: CalendarEvent[];
    onEventClick?: (event: CalendarEvent) => void;
    onDateClick?: (date: Date) => void;
}

export const Calendar: React.FC<CalendarProps> = ({ events, onEventClick, onDateClick }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const calendarData = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const endDate = new Date(lastDay);
        endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

        const days = [];
        const current = new Date(startDate);

        while (current <= endDate) {
            const dayEvents = events.filter(event => {
                const eventDate = new Date(event.date);
                return eventDate.toDateString() === current.toDateString();
            });

            days.push({
                date: new Date(current),
                isCurrentMonth: current.getMonth() === month,
                isToday: current.toDateString() === new Date().toDateString(),
                isSelected: selectedDate && current.toDateString() === selectedDate.toDateString(),
                events: dayEvents
            });

            current.setDate(current.getDate() + 1);
        }

        return days;
    }, [currentDate, events, selectedDate]);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const goToPreviousMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        onDateClick?.(date);
    };

    const getEventColor = (type: string) => {
        switch (type) {
            case 'precall':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'postcall':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'visit':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Calendar Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Button
                            onClick={goToPreviousMonth}
                            plain
                            className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <ChevronLeftIcon className="h-5 w-5" />
                        </Button>
                        <Button
                            onClick={goToNextMonth}
                            plain
                            className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <ChevronRightIcon className="h-5 w-5" />
                        </Button>
                        <h2 className="text-xl font-semibold text-gray-900">
                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </h2>
                        {selectedDate && (
                            <div className="text-sm text-blue-600 font-medium">
                                Selected: {selectedDate.toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center space-x-3">
                        {selectedDate && (
                            <Button
                                onClick={() => setSelectedDate(null)}
                                plain
                                className="text-sm text-gray-600 hover:text-gray-800"
                            >
                                Clear Selection
                            </Button>
                        )}
                        <Button
                            onClick={goToToday}
                            outline
                        >
                            Today
                        </Button>
                    </div>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="px-6 py-4">
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-px mb-2">
                    {dayNames.map(day => (
                        <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                    {calendarData.map((day, index) => (
                        <div
                            key={index}
                            className={`min-h-[200px] bg-white p-2 transition-all duration-200 hover:bg-gray-50 cursor-pointer ${!day.isCurrentMonth ? 'bg-gray-50' : ''
                                } ${day.isToday ? 'ring-2 ring-blue-500 ring-inset' : ''
                                } ${day.isSelected ? 'ring-2 ring-blue-600 ring-inset bg-blue-50' : ''
                                }`}
                            onClick={() => handleDateClick(day.date)}
                        >
                            <div className={`text-sm font-medium mb-1 ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                                } ${day.isToday ? 'text-blue-600 font-bold' : ''
                                } ${day.isSelected ? 'text-blue-700 font-bold' : ''
                                }`}>
                                {day.date.getDate()}
                                {day.events.length > 0 && (
                                    <span className="ml-1 text-xs text-gray-500">
                                        ({day.events.length})
                                    </span>
                                )}
                            </div>

                            {/* Events */}
                            <div className="space-y-1 max-h-[150px] overflow-y-auto calendar-events-scrollbar">
                                {day.events.map((event, eventIndex) => (
                                    <div
                                        key={event.id}
                                        className={`text-xs px-1.5 py-0.5 rounded border cursor-pointer hover:opacity-80 transition-all duration-150 hover:scale-105 ${getEventColor(event.type)}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEventClick?.(event);
                                        }}
                                        title={`${event.title} - ${event.customer || 'Unknown Customer'}`}
                                    >
                                        <div className="font-medium truncate text-xs leading-tight">{event.title}</div>
                                        {event.customer && (
                                            <div className="text-xs opacity-75 truncate leading-tight">{event.customer}</div>
                                        )}
                                    </div>
                                ))}
                                {day.events.length === 0 && (
                                    <div className="text-xs text-gray-400 text-center py-2">
                                        No events
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Legend */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded"></div>
                        <span>Pre-Call Plans</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-100 border border-green-200 rounded"></div>
                        <span>Post-Call Plans</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-purple-100 border border-purple-200 rounded"></div>
                        <span>Visits</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
